import mapKakao from '@/assets/figma/accounts/map-kakao.png';
import mapNaver from '@/assets/figma/accounts/map-naver.png';
import type { PharmacyInfo } from '@/entities/business-relationship/api/businessRelationship.api';
import { EmptyState } from '@/shared/components/StateViews';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { HelpText } from '@/shared/components/ui/help-text';
import { Icon } from '@/shared/components/ui/icon';
import { cn } from '@/shared/lib/utils';
import { useEffect, useRef, useState } from 'react';

import { copyWithToast } from '../utils';

/**
 * M-21 거래처 헤더 팝업 — 주소(848:8714) · 사업자등록증(835:10415) · 문전약국(835:10288). 모두 폭 640 · r20.
 * 셋 다 조회 전용이고 ✕ · ESC · 배경 클릭으로 닫힌다. 등록 · 수정은 앱에서만 — 버튼 대신 안내 문구(helptext)만 둔다.
 */

/** 팝업 오른쪽 위 ✕ (Figma btn 40 · top/right 8) */
const CloseButton = () => (
  <DialogClose asChild>
    <Button
      variant="text"
      className="absolute top-2 right-2"
      aria-label="닫기"
      leftIcon={<Icon name="cancel-line-24" />}
    />
  </DialogClose>
);

const POPUP_CLASS = 'lg:max-w-[640px] lg:rounded-20';

const mapLinks = (address: string) => [
  {
    label: '네이버 지도 열기',
    logo: mapNaver,
    href: `https://map.naver.com/p/search/${encodeURIComponent(address)}`,
  },
  {
    label: '카카오 지도 열기',
    logo: mapKakao,
    href: `https://map.kakao.com/?q=${encodeURIComponent(address)}`,
  },
];

/** 주소 팝업 (Figma 848:8714) — 지도는 주소를 검색어로 넘겨 새 탭에서 연다. 앱의 T map은 뺐다 */
export const AddressDialog = ({
  open,
  onOpenChange,
  clinicName,
  address,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicName: string;
  address: string;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent hideClose className={cn(POPUP_CLASS, 'gap-6 p-10')}>
      <div className="flex flex-col gap-2 pr-6">
        <DialogTitle className="text-heading-20-b text-fg-primary">
          {clinicName}
        </DialogTitle>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-body-16-r break-keep text-fg-primary">{address}</p>
          <Button
            variant="grey"
            leftIcon={<Icon name="copy-line-24" />}
            onClick={() =>
              void copyWithToast(
                address,
                '주소가 복사되었어요.',
                '주소를 복사하지 못했어요.',
              )
            }
          >
            복사
          </Button>
        </div>
      </div>
      <DialogDescription className="sr-only">
        {clinicName} 주소와 지도 바로가기
      </DialogDescription>
      <div className="flex flex-col gap-2">
        {mapLinks(address).map((l) => (
          <a
            key={l.label}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 rounded-20 bg-surface-grey p-4 text-body-16-m text-fg-primary transition-colors outline-none hover:bg-grey-100 focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)]"
          >
            <img src={l.logo} alt="" className="size-10 shrink-0 rounded-12" />
            {l.label}
          </a>
        ))}
      </div>
      <CloseButton />
    </DialogContent>
  </Dialog>
);

const ZOOM_STEPS = [25, 50, 75, 100, 125, 150, 200, 300, 400];
const ZOOM_MIN = ZOOM_STEPS[0];
const ZOOM_MAX = ZOOM_STEPS[ZOOM_STEPS.length - 1];

/**
 * 사업자등록증 팝업 (Figma 835:10415, 640) — 열람만 제공한다. 수정은 앱에서만 — v1.0은 [사업자등록증 수정] 버튼 없이 안내 문구만 둔다.
 * 확대 · 축소는 25~400%. 처음과 [맞춤]은 뷰어 영역에 꽉 맞춰(작은 이미지도 키워서) 보여주고, [원본 크기]는 100%.
 * PDF도 #view=Fit으로 한 페이지가 뷰어에 맞게 보이도록 연다.
 * 이미지는 presigned URL(10분 만료)이라 실패하면 상세를 한 번 자동으로 다시 받아 새 URL로 재시도한다.
 * 화면을 연 지 10분이 지나 URL이 만료된 경우가 가장 흔하다. 재시도도 실패하면 오류 화면을 보여 준다.
 * 앱에서 PDF로 올린 등록증도 있어 PDF는 <img> 대신 브라우저 PDF 뷰어(iframe)로 보여 준다.
 */
const isPdfUrl = (url: string) => {
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.pdf');
  } catch {
    return url.split('?')[0].toLowerCase().endsWith('.pdf');
  }
};

export const BusinessLicenseDialog = ({
  open,
  onOpenChange,
  clinicName,
  imageUrl,
  onReload,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicName: string;
  imageUrl: string;
  onReload: () => void;
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  /** 맞춤 상태의 실제 배율(%) — 확대 · 축소의 출발점. 이미지가 뜰 때 잰다 */
  const [fit, setFit] = useState(100);
  /** null = 맞춤 */
  const [zoom, setZoom] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  /** 만료 대비 자동 재시도는 팝업을 열 때마다 한 번만 한다 */
  const [retried, setRetried] = useState(false);
  const pdf = isPdfUrl(imageUrl);

  useEffect(() => {
    if (!open) {
      setZoom(null);
      setRetried(false);
    }
  }, [open]);

  useEffect(() => {
    setFailed(false);
    setNatural(null);
  }, [imageUrl]);

  const handleImageError = () => {
    if (!retried) {
      setRetried(true);
      onReload(); // 새 presigned URL을 받으면 imageUrl이 바뀌어 다시 그린다
      return;
    }
    setFailed(true);
  };

  const currentPercent = zoom ?? fit;

  const measureFit = (w: number, h: number) => {
    const el = viewportRef.current;
    if (!el || !w || !h) return;
    // 작은 이미지도 뷰어 크기에 맞춰 키운다 (상한 없음)
    setFit(Math.min(el.clientWidth / w, el.clientHeight / h) * 100);
  };

  const zoomIn = () => {
    const next = ZOOM_STEPS.find((s) => s > currentPercent + 0.5) ?? ZOOM_MAX;
    setZoom(next);
  };
  const zoomOut = () => {
    const prev =
      [...ZOOM_STEPS].reverse().find((s) => s < currentPercent - 0.5) ??
      ZOOM_MIN;
    setZoom(prev);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className={cn(POPUP_CLASS, 'items-center gap-6 p-6 lg:max-h-[94vh]')}
      >
        <DialogTitle className="w-full pr-8 text-heading-20-b text-fg-primary">
          {clinicName}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {clinicName} 사업자등록증 이미지
        </DialogDescription>
        <div
          ref={viewportRef}
          className={cn(
            'relative h-[62dvh] min-h-[320px] w-full overflow-auto border border-line-grey bg-surface-grey lg:h-[min(64vh,790px)]',
            zoom === null && 'flex items-center justify-center',
          )}
        >
          {failed ? (
            <EmptyState
              className="h-full"
              message="이미지를 불러오지 못했어요."
              action={
                <Button variant="grey" size="small" onClick={onReload}>
                  다시 불러오기
                </Button>
              }
            />
          ) : pdf ? (
            <iframe
              // 브라우저 PDF 뷰어에 "페이지 전체 맞춤"으로 열라고 알려 준다 (fragment라 서명에 영향 없음)
              src={`${imageUrl}#view=Fit`}
              title={`${clinicName} 사업자등록증 (PDF)`}
              className="h-full w-full bg-surface-white"
            />
          ) : (
            <img
              src={imageUrl}
              alt={`${clinicName} 사업자등록증`}
              draggable={false}
              onLoad={(e) => {
                const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                setNatural({ w, h });
                measureFit(w, h);
              }}
              onError={handleImageError}
              className={cn(
                'select-none',
                zoom === null ? 'h-full w-full object-contain' : 'max-w-none',
              )}
              style={
                zoom !== null && natural
                  ? { width: (natural.w * zoom) / 100 }
                  : undefined
              }
            />
          )}
        </div>
        {/* 확대 · 축소 막대 (Figma Tab 835:10426) — PDF는 브라우저 뷰어가 맡는다 */}
        {pdf ? (
          <a
            href={imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-button-14-sb text-fg-accent hover:underline"
          >
            새 탭에서 열기
          </a>
        ) : (
          <div className="flex items-center gap-2 rounded-16 bg-surface-grey p-1">
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="small"
                aria-label="축소"
                leftIcon={<Icon name="minus-line-16" />}
                disabled={failed || currentPercent <= ZOOM_MIN}
                onClick={zoomOut}
              />
              <span className="min-w-[52px] px-1 text-center text-button-16-sb text-fg-tertiary tabular-nums">
                {Math.round(currentPercent)}%
              </span>
              <Button
                variant="outline"
                size="small"
                aria-label="확대"
                leftIcon={<Icon name="plus-line-16" />}
                disabled={failed || currentPercent >= ZOOM_MAX}
                onClick={zoomIn}
              />
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="small"
                disabled={failed}
                aria-pressed={zoom === null}
                onClick={() => {
                  setZoom(null);
                  if (natural) measureFit(natural.w, natural.h);
                }}
              >
                맞춤
              </Button>
              <Button
                variant="outline"
                size="small"
                disabled={failed}
                aria-pressed={zoom === 100}
                onClick={() => setZoom(100)}
              >
                원본 크기
              </Button>
            </div>
          </div>
        )}
        <HelpText tone="grey" className="w-full">
          사업자등록증 수정은 현재 앱에서만 가능해요.
        </HelpText>
        <CloseButton />
      </DialogContent>
    </Dialog>
  );
};

/** Figma 거래처_문전약국 (835:10351) — 약국명 15 SemiBold (+ 대표 배지 835:10350) · 주소 13 Regular */
const PharmacyRow = ({ pharmacy }: { pharmacy: PharmacyInfo }) => {
  const unknown = !!pharmacy.closedOn;
  return (
    <li className="flex flex-col gap-1 py-3">
      <p
        className={cn(
          'text-body-15-sb break-keep',
          unknown ? 'text-fg-light' : 'text-fg-primary',
        )}
      >
        {pharmacy.pharmacyName}
      </p>
      <p className="text-body-13-r text-fg-tertiary">
        {unknown || !pharmacy.address ? '정보 없음' : pharmacy.address}
      </p>
    </li>
  );
};

/**
 * 문전약국 팝업 (Figma 835:10288) — 조회만. 등록 · 해제와 「최소 1곳 유지」 규칙은 앱에서 적용한다.
 * v1.0: 앱 전용 [문전약국 설정] 버튼을 빼고 안내 문구만 둔다. 폐업 · 삭제로 약국 정보가 없으면 「정보 없음」.
 * 대표 약국 배지(Type=대표)는 API에 대표 여부가 없어 그리지 않는다.
 */
export const PharmacyDialog = ({
  open,
  onOpenChange,
  pharmacies,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pharmacies: PharmacyInfo[];
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent hideClose className={cn(POPUP_CLASS, 'gap-6 p-10')}>
      <DialogTitle className="flex gap-2 text-heading-20-b text-fg-primary">
        문전약국
        <span className="text-fg-accent">{pharmacies.length}곳</span>
      </DialogTitle>
      <DialogDescription className="sr-only">
        연결된 문전약국 목록
      </DialogDescription>
      <div className="-my-3 min-h-0 flex-1 overflow-y-auto">
        {pharmacies.length === 0 ? (
          <EmptyState className="py-10" message="연결된 문전약국이 없어요." />
        ) : (
          <ul>
            {pharmacies.map((p) => (
              <PharmacyRow key={p.pharmacyId} pharmacy={p} />
            ))}
          </ul>
        )}
      </div>
      <HelpText tone="grey">문전약국 등록은 현재 앱에서만 가능해요.</HelpText>
      <CloseButton />
    </DialogContent>
  </Dialog>
);
